/* eslint-disable react/display-name */
import { Fragment, useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useQuery, useMutation } from "@apollo/client";
import { useHistory, useParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Nav from "../Nav/Nav";
import styles from "./JoinBrand.module.scss";
import { ConsultantPaths as Paths, AuthPaths } from "../../../constants/paths";
import Button, { LIGHT_ALT } from "../../common/Button/Button";
import useAuthentication from "../../../hooks/useAuthentication";
import ClientContactService from "../../../service/clientContact";
import ImageIcon, { CIRCLE } from "../../common/ImageIcon/ImageIcon";
import Loader from "../../Loader";
import Error, { parseError } from "../../common/Error/Error";
import { UserType } from "../../../constants/userType";

const JoinBrand = () => {
  const history = useHistory();
  const { id: clientConsultantId } = useParams();
  const isAuthenticated = useAuthentication();
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!clientConsultantId) {
      history.replace(Paths.dashboard());
    }
  }, [clientConsultantId, history]);

  const { loading, data } = useQuery(ClientContactService.get, {
    variables: { clientConsultantId },
    skip: !clientConsultantId,
    fetchPolicy: "network-only", // Force network request instead of cache
    onError: () => {
      console.log("Error in query");
    },
  });

  const [acceptInvitation, { loading: acceptLoading }] = useMutation(
    ClientContactService.acceptInvitation,
    {
      onCompleted: () => {
        setErrorMessage(""); // Clear any previous errors
        history.push(Paths.dashboard());
      },
      onError: (error) => {
        console.error("Error accepting invitation:", error);
        setErrorMessage(
          error.message || "An error occurred while processing your request. Please try again.",
        );
      },
    },
  );

  const handleLoginRedirect = () => {
    const currentUrl = `${window.location.pathname}`;
    const loginPath = AuthPaths.loginUserType(UserType.CONSULTANT);
    history.push(`${loginPath}?returnUrl=${encodeURIComponent(currentUrl)}`);
  };

  const handleAccept = async () => {
    try {
      setErrorMessage(""); // Clear any previous errors
      await acceptInvitation({
        variables: {
          clientConsultantId,
          status: "ACTIVE",
        },
      });
    } catch (error) {
      console.error("Error accepting invitation:", error);
      setErrorMessage(
        error.message || "An error occurred while accepting the invitation. Please try again.",
      );
    }
  };

  const handleDecline = async () => {
    try {
      setErrorMessage(""); // Clear any previous errors
      await acceptInvitation({
        variables: {
          clientConsultantId,
          status: "REMOVED",
        },
      });
      history.push(Paths.dashboard());
    } catch (error) {
      console.error("Error declining invitation:", error);
      setErrorMessage(
        error.message || "An error occurred while declining the invitation. Please try again.",
      );
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (!data?.clientContact) {
    return <Error />;
  }

  return (
    <Fragment>
      <Nav showBack={false} />
      <div className="container h-100 d-flex align-items-center justify-content-center">
        <div className="row w-100 justify-content-center">
          <div className="col-md-6 text-center">
            <div className={styles.logoContainer}>
              {data.clientContact.logo && (
                <ImageIcon
                  variant={CIRCLE}
                  src={data.clientContact.logo}
                  width={120}
                  className="mt-4 mb-2"
                />
              )}
            </div>
            <div className={`${styles.heading} font-weight-700 mb-4`}>
              <FormattedMessage
                id="joinBrand.heading"
                values={{ brandName: data.clientContact.businessName }}
              />
            </div>
            <div className="mb-5 font-size-18">
              <FormattedMessage id="joinBrand.description" />
            </div>
            <div className="d-flex justify-content-center">
              <Button
                onClick={isAuthenticated ? handleAccept : handleLoginRedirect}
                variant={LIGHT_ALT}
                className="space-letters mx-2"
                disabled={acceptLoading}
              >
                <span className={styles.buttonText}>
                  <FormattedMessage
                    id={isAuthenticated ? "joinBrand.accept" : "joinBrand.login"}
                    defaultMessage={isAuthenticated ? "Accept Invitation" : "Log in to Accept"}
                  />
                </span>
              </Button>
              {isAuthenticated && (
                <Button
                  onClick={() => setShowDeclineModal(true)}
                  variant="danger"
                  className="space-letters mx-2"
                  disabled={acceptLoading}
                >
                  <span className={styles.buttonText}>
                    <FormattedMessage id="joinBrand.decline" defaultMessage="Decline Invitation" />
                  </span>
                </Button>
              )}
            </div>
            {errorMessage && (
              <div className="alert alert-danger mb-3 mt-5" role="alert">
                {parseError(errorMessage)}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={showDeclineModal} onHide={() => setShowDeclineModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage
              id="joinBrand.declineModal.title"
              defaultMessage="Decline Invitation?"
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <FormattedMessage
              id="joinBrand.declineModal.message"
              defaultMessage="Are you sure you want to decline this invitation? You won't be able to join this brand later."
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-center w-100">
            <Button
              onClick={() => setShowDeclineModal(false)}
              variant={LIGHT_ALT}
              className="space-letters mx-2"
            >
              <FormattedMessage id="joinBrand.declineModal.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              onClick={() => {
                handleDecline();
                setShowDeclineModal(false);
              }}
              variant="danger"
              className="space-letters mx-2"
              disabled={acceptLoading}
            >
              <FormattedMessage id="joinBrand.declineModal.confirm" defaultMessage="Yes, Decline" />
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default JoinBrand;
